<template>
  <span
    ref="root"
    class="cia"
  >
    <!-- type word -->
    <span
      class="cia__word cia__word--type"
      @click.stop="openPicker('type')"
    >{{ argTypeLabel }}</span>

    <!-- entity field word (entity / paramentity / sourceentity) -->
    <span
      v-if="isEntityType"
      class="cia__word"
      :class="{ 'cia__word--empty': !hasArgEntity }"
      @click.stop="openPicker('entity')"
    >{{ argEntityLabel }}</span>

    <!-- value inputs -->
    <template v-else-if="isValueType">
      <!-- bit checkbox (datatype=bit or value is boolean) -->
      <b-form-checkbox
        v-if="arg.datatype === 'bit' || typeof arg.value === 'boolean'"
        v-model="arg.value"
        class="cia__check"
        @change="$emit('change', { kind: 'value', arg })"
        @click.native.stop
      />
      <!-- ref picker word -->
      <span
        v-else-if="isRefType"
        class="cia__word"
        :class="{ 'cia__word--empty': arg.value == null }"
        @click.stop="openPicker('ref')"
      >{{ refValueLabel }}</span>
      <!-- null value display -->
      <span
        v-else-if="arg.value === null || arg.value === undefined"
        class="cia__word cia__word--empty"
        @click.stop="arg.value = ''"
      >null</span>
      <!-- plain text input -->
      <b-form-input
        v-else
        v-model="arg.value"
        size="sm"
        class="cia__input"
        placeholder="значение"
        @click.native.stop
      />
    </template>

    <!-- fallback text input for other types -->
    <b-form-input
      v-else
      v-model="arg.value"
      size="sm"
      class="cia__input"
      :placeholder="arg.type ? arg.type.name : 'значение'"
      @click.native.stop
    />

    <!-- dropdown picker panel -->
    <div
      v-if="activePicker"
      class="cia__panel"
      @click.stop
    >
      <b-form-input
        ref="searchInput"
        v-model="search"
        size="sm"
        placeholder="Поиск..."
        autocomplete="off"
        @input="onSearch"
        @keydown.esc.native="closePicker"
        @keydown.enter.native.prevent="pickFirst"
      />
      <div
        v-if="filtered.length"
        class="cia__list"
      >
        <div
          v-for="item in filtered"
          :key="item.value"
          class="cia__opt"
          @click.stop="pick(item)"
        >{{ item.text }}</div>
      </div>
      <div
        v-else-if="search"
        class="cia__empty"
      >Не найдено</div>
    </div>
  </span>
</template>

<script>
import { BFormInput, BFormCheckbox } from 'bootstrap-vue'

export default {
  name: 'CbsInlineArgument',
  components: { BFormInput, BFormCheckbox },
  props: {
    arg: { type: Object, required: true },
    argTypes: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    paramFields: { type: Array, default: () => [] },
    allEntities: { type: Array, default: () => [] },
    refs: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      activePicker: null,
      search: '',
      source: [],
      filtered: [],
    }
  },
  computed: {
    isEntityType() {
      const s = this.arg.type && this.arg.type.sid
      return s === 'entity' || s === 'paramentity' || s === 'sourceentity'
    },
    isValueType() {
      return this.arg.type && this.arg.type.sid === 'value'
    },
    isRefType() {
      return (this.arg.datatype === 'ref' || this.arg.datatype === 'refsid') && this.arg.entityid
    },
    argTypeLabel() {
      const sid = this.arg.type && this.arg.type.sid
      const ruMap = {
        entity: 'Поле', value: 'Значение', paramentity: 'Параметр', sourceentity: 'Источник', formula: 'Формула', scalar: 'Скаляр', function: 'Функция',
      }
      if (sid && ruMap[sid]) return ruMap[sid]
      return (this.arg.type && (this.arg.type.name || this.arg.type.sid)) || '(тип)'
    },
    argEntityLabel() {
      const { arg } = this
      // arg.value is object with title (rich format)
      if (arg.value && typeof arg.value === 'object') {
        return arg.value.title || arg.value.entitysid || '(поле)'
      }
      // arg.value is numeric entity ID (from form_param filters)
      if (arg.value != null && typeof arg.value !== 'object') {
        const sid = arg.type && arg.type.sid
        const src = (sid === 'paramentity') ? this.paramFields : this.fields
        const fld = src.find(f => f.value === Number(arg.value)) || src.find(f => f.value === arg.value)
        if (fld) return fld.title || fld.entitysid || String(arg.value)
        // Try allEntities
        const ent = this.allEntities.find(e => e.value === Number(arg.value) || e.id === Number(arg.value))
        if (ent) return ent.title || ent.name || String(arg.value)
      }
      // Fallback: try entitysid
      if (arg.entitysid) {
        const fld = this.fields.find(f => f.entitysid === arg.entitysid)
        return (fld && (fld.title || fld.entitysid)) || arg.entitysid
      }
      return '(выберите поле)'
    },
    hasArgEntity() {
      const { arg } = this
      if (arg.value && typeof arg.value === 'object') return !!(arg.value.title || arg.value.entitysid)
      return !!arg.entitysid
    },
    refValueLabel() {
      const { arg } = this
      if (arg.value == null) return '(значение)'
      const ref = this.refs[arg.entityid]
      if (ref) {
        const found = ref.find(r => String(r.value) === String(arg.value))
        if (found) return found.title || String(found.value)
      }
      return String(arg.value)
    },
  },
  watch: {
    isRefType(val) {
      if (val && this.arg.entityid) this.$emit('ensure-ref', this.arg.entityid)
    },
  },
  mounted() {
    if (this.isRefType && this.arg.entityid) this.$emit('ensure-ref', this.arg.entityid)
    this._outsideClick = e => {
      if (this.activePicker && !this.$el.contains(e.target)) this.closePicker()
    }
    document.addEventListener('click', this._outsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this._outsideClick)
  },
  methods: {
    openPicker(kind) {
      if (this.activePicker === kind) { this.closePicker(); return }
      this.search = ''
      this.activePicker = kind
      this.source = this.buildSource(kind)
      this.filtered = this.source
      this.$nextTick(() => {
        const el = this.$refs.searchInput
        if (el) (Array.isArray(el) ? el[0] : el).focus()
      })
    },
    closePicker() {
      this.activePicker = null
      this.search = ''
      this.source = []
      this.filtered = []
    },
    onSearch() {
      if (!this.search.trim()) { this.filtered = this.source; return }
      const re = new RegExp(
        this.search.trim().toLowerCase().split(/\s+/).map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
          .join('.*'),
      )
      this.filtered = this.source.filter(i => re.test((i.text || '').toLowerCase()))
    },
    buildSource(kind) {
      if (kind === 'type') {
        return this.argTypes.map(t => ({ value: t.sid, text: t.name, _t: t }))
      }
      if (kind === 'entity') {
        const sid = this.arg.type && this.arg.type.sid
        let src
        if (sid === 'sourceentity') src = this.allEntities
        else if (sid === 'paramentity') src = this.paramFields
        else src = this.fields
        return src.map(f => ({ value: f.value, text: f.title || f.entitysid || String(f.value), _f: f }))
      }
      if (kind === 'ref') {
        const ref = this.refs[this.arg.entityid]
        if (!ref) return []
        return ref.map(r => ({ value: r.value, text: r.title || String(r.value), _r: r }))
      }
      return []
    },
    pickFirst() {
      if (this.filtered.length) this.pick(this.filtered[0])
    },
    pick(item) {
      const kind = this.activePicker
      if (kind === 'type') {
        const t = item._t
        this.$set(this.arg, 'type', { id: t.id, sid: t.sid, name: t.name })
        this.arg.value = null
        this.arg.datatype = null
        this.arg.entityid = null
        this.arg.entitysid = null
        this.arg.parententityid = null
        this.$emit('change', { kind: 'type', arg: this.arg })
      } else if (kind === 'entity') {
        const fld = item._f
        const typeSid = this.arg.type && this.arg.type.sid
        const preserveType = typeSid === 'sourceentity' || typeSid === 'paramentity'
        if (!preserveType) this.$set(this.arg, 'type', { id: 1, sid: 'entity', name: 'Entity' })
        this.arg.value = fld || null
        this.arg.entitysid = fld ? fld.entitysid : null
        this.arg.entityid = null
        this.arg.parententityid = null
        this.arg.datatype = null
        this.$emit('change', {
          kind: 'entity', arg: this.arg, field: fld, preserveType,
        })
      } else if (kind === 'ref') {
        const r = item._r || item
        this.arg.value = r.value
        this.$emit('change', { kind: 'ref', arg: this.arg })
      }
      this.closePicker()
    },
  },
}
</script>

<style lang="scss" scoped>
.cia {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.cia__word {
  color: #7367f0;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  border-bottom: 1px dashed rgba(115, 103, 240, 0.4);
  &:hover { border-bottom-color: #7367f0; }
}

.cia__word--empty {
  color: #b9b9c3;
  border-bottom-color: rgba(185, 185, 195, 0.4);
  font-style: italic;
  &:hover { color: #7367f0; border-bottom-color: #7367f0; }
}

.cia__word--type {
  font-size: 11px;
  color: #82868b;
  border-bottom-color: rgba(130, 134, 139, 0.3);
  &:hover { color: #7367f0; border-bottom-color: #7367f0; }
}

.cia__input {
  font-size: 12px;
  height: 22px;
  padding: 0 4px;
  max-width: 140px;
}

.cia__check {
  margin: 0;
}

.cia__panel {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  width: 220px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
}

.cia__list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.cia__opt {
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 3px;
  color: #5e5873;
  &:hover { background: #f8f8f8; }
}

.cia__empty {
  padding: 6px 8px;
  color: #b9b9c3;
}

.dark-layout {
  .cia__word { color: #7367f0; }
  .cia__word--empty { color: #4b4b5a; }
  .cia__word--type { color: #676d7d; }
  .cia__panel {
    background: #283046;
    border-color: #3b4253;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  .cia__opt {
    color: #d0d2d6;
    &:hover { background: #3b4253; }
  }
}
</style>
